import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserJourney } from "@/types/analytics.types";

interface UseUserJourneysOptions {
  startDate: string;
  endDate: string;
  limit?: number;
}

export const useUserJourneys = ({ startDate, endDate, limit = 10 }: UseUserJourneysOptions) => {
  return useQuery({
    queryKey: ['user-journeys', startDate, endDate, limit],
    queryFn: async (): Promise<UserJourney[]> => {
      // Fetch all events grouped by session
      const { data: events, error } = await supabase
        .from('analytics_events')
        .select('session_id, page_path, event_name, created_at')
        .not('session_id', 'is', null)
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!events || events.length === 0) return [];

      // Group events by session
      const sessionMap = new Map<string, any[]>();
      events.forEach(event => {
        if (!event.session_id) return;
        
        if (!sessionMap.has(event.session_id)) {
          sessionMap.set(event.session_id, []);
        }
        sessionMap.get(event.session_id)!.push(event);
      });

      // Create journey paths
      const journeyMap = new Map<string, { count: number; conversions: number }>();
      
      sessionMap.forEach((sessionEvents, sessionId) => {
        // Get unique pages visited in order
        const pages = Array.from(new Set(
          sessionEvents
            .filter(e => e.page_path)
            .map(e => e.page_path)
        ));

        if (pages.length === 0) return;

        // Create path key (limit to first 5 steps for simplicity)
        const pathKey = pages.slice(0, 5).join(' → ');

        // Check if session had a conversion
        const hasConversion = sessionEvents.some(
          e => e.event_name === 'lead_captured' || e.event_name === 'consultation_requested'
        );

        if (!journeyMap.has(pathKey)) {
          journeyMap.set(pathKey, { count: 0, conversions: 0 });
        }

        const journey = journeyMap.get(pathKey)!;
        journey.count += 1;
        if (hasConversion) journey.conversions += 1;
      });

      // Convert to array and sort by count
      const journeys: UserJourney[] = Array.from(journeyMap.entries())
        .map(([path, data]) => ({
          path: path.split(' → '),
          count: data.count,
          conversion_rate: data.count > 0 ? (data.conversions / data.count) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);

      return journeys;
    },
    refetchInterval: 120000, // Refetch every 2 minutes
  });
};
